/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import MemoClose from '../Icons/Close'
import MemoCheck from '../Icons/Check'

import MemoNotificationsOutlined from '../Icons/NotificationsOutlined'

import { useTheme } from '../../../index'
import { useNotificationsHomeContext } from '../../context'
import { useGetTypography } from '../../../hooks/styles/useGetTypography'

import { IconButton, Collapsible } from '../../../components'

import { InAppNotification } from '../InAppNotification'
import { useNotificationsListStyles } from '../../../hooks/styles/useNotificationsListStyles'
import { useBaseStyles } from '../../../hooks/styles/useBaseStyles'

export const LinkWrapper = ({
  children,
  link,
  hover = false,
  sameTab = 'false',
  item
}) => {
  const theme = useTheme()
  const hasAttachment = link?.length > 0 && hover

  const {
    handlers: { handleMarkAsRead }
  } = useNotificationsHomeContext()

  const [showHover, setShowHover] = useState(false)

  const handleMouseEnter = () => {
    if (hasAttachment) {
      setShowHover(true)
    }
  }

  const handleMouseLeave = () => {
    setShowHover(false)
  }

  if (!hasAttachment) {
    // If no attachment, render children as-is
    return <>{children}</>
  }

  return (
    <div
      onClick={(e) => {
        handleMarkAsRead(item)
        if (sameTab === 'true') {
          window.open(link, '_self')
        } else {
          window.open(link, '_blank')
        }
        e.stopPropagation()
        e.preventDefault()
      }}
      style={{
        position: 'relative',
        cursor: 'pointer',
        display: 'inline-block'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        style={{
          transition: 'filter 0.2s ease-in-out',
          filter: showHover ? 'brightness(70%) blur(0.5px)' : 'none'
        }}
      >
        {children}
      </div>

      {showHover && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            bottom: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '4px',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        >
          <svg
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='white'
            style={{
              fontSize: '1rem'
            }}
          >
            <path d='M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z' />
          </svg>
        </div>
      )}
    </div>
  )
}

const EmptyList = () => {
  const {
    data: { tabPanelValue }
  } = useNotificationsHomeContext()

  const { Body2 } = useGetTypography()
  const { Spacer } = useBaseStyles()

  const theme = useTheme()
  const tabIsUnread = tabPanelValue === 'unread'

  return (
    <div
      style={{
        height: '100%',
        minHeight: '300px',
        color: theme.palette.secondary.main,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        position: 'relative'
      }}
      className='no-messages-section'
    >
      <MemoNotificationsOutlined fontSize='large' />
      <Spacer mx='0.2rem' />
      <Body2
        style={{
          color: theme.palette.secondary.main
        }}
      >
        No {tabIsUnread ? 'unread' : ''} notifications{' '}
        {tabIsUnread ? '' : 'to show yet'}
      </Body2>
    </div>
  )
}

const DeleteAllNotifications = ({ inProp }) => {
  const { DeleteNotificationsContainer, DeleteNotificationsTypography } =
    useNotificationsListStyles()

  const {
    handlers: { deleteAllMessages, handleClickDelete }
  } = useNotificationsHomeContext()

  return (
    <Collapsible in={inProp}>
      <DeleteNotificationsContainer>
        <DeleteNotificationsTypography>
          Are you sure you want to delete all the notifications?
        </DeleteNotificationsTypography>
        <div style={{ display: 'flex', gap: 1, marginRight: '1rem' }}>
          <IconButton onClick={(e) => deleteAllMessages(e)}>
            <MemoCheck />
          </IconButton>
          <IconButton onClick={(e) => handleClickDelete(e)}>
            <MemoClose />
          </IconButton>
        </div>
      </DeleteNotificationsContainer>
    </Collapsible>
  )
}

export const NotificationsList = ({ filter }) => {
  const theme = useTheme()
  const { ref, inView } = useInView()

  const {
    data: {
      list,
      unreadList,
      count,
      openDeleteDialog,
      tabPanelValue,
      unreadCount,
      page,
      selectedLabel
    },
    handlers: { loadMoreNotifications, setSelectedLabel }
  } = useNotificationsHomeContext()

  const { CollapsibleWrapper, CustomTypography, OverlayWrapper } =
    useBaseStyles()

  const { NotificationsListContainer, ShowAllNotificationsContainer } =
    useNotificationsListStyles()

  const mapperList = filter ? unreadList : list

  const sortedList = mapperList.sort((a, b) => {
    const aPinned = a.notification_content?.is_pinned === true
    const bPinned = b.notification_content?.is_pinned === true

    return Number(bPinned) - Number(aPinned)
  })

  const checkCount = tabPanelValue === 'all' ? count : unreadCount

  useEffect(() => {
    if (inView && sortedList?.length < checkCount && sortedList?.length > 0) {
      loadMoreNotifications(page, tabPanelValue)
    }

    if (
      !sortedList?.find(
        (item) => item?.notification_content?.label === selectedLabel
      )
    ) {
      setSelectedLabel('')
    }
  }, [inView, sortedList?.length, checkCount, selectedLabel])

  // eslint-disable-next-line no-unused-vars
  if (sortedList?.length > 0) {
    return (
      <NotificationsListContainer>
        <CollapsibleWrapper inProp={openDeleteDialog}>
          <DeleteAllNotifications inProp={openDeleteDialog} />
        </CollapsibleWrapper>
        <OverlayWrapper inProp={openDeleteDialog}>
          <div style={{ alignContent: 'flex-start' }}>
            {sortedList
              ?.filter((item) => {
                if (selectedLabel) {
                  return item?.notification_content?.label === selectedLabel
                } else {
                  return true
                }
              })
              .map((item, index) => (
                <div key={item?.msg_id}>
                  <InAppNotification item={item} index={index} />
                </div>
              ))}

            {sortedList?.length === checkCount && sortedList?.length > 19 && (
              <ShowAllNotificationsContainer>
                <CustomTypography fontSize='13px'>
                  Showing all notifications
                </CustomTypography>
              </ShowAllNotificationsContainer>
            )}
          </div>
        </OverlayWrapper>
      </NotificationsListContainer>
    )
  }

  return <EmptyList />
}

export default React.memo(NotificationsList)
